/** Pure figures behind the Class performance overview (design CP "Cohort at a glance"). */

interface Bucket { bucket: string; count: number }

const clamp = (v: number) => Math.max(0, Math.min(100, v));

/** Current average minus the average of the window before it; null when either is missing. */
export function priorDelta(current: number | null | undefined, prior: number | null | undefined): number | null {
  if (current == null || prior == null || !Number.isFinite(current) || !Number.isFinite(prior)) return null;
  return current - prior;
}

/** "↑ 2.6" / "↓ 1.4" (design `.chip.c-up` / `.c-down`); zero reads as up. */
export function formatPtsDelta(d: number): string {
  const r = Math.round(d * 10) / 10;
  if (r === 0) return '±0.0';
  return `${r > 0 ? '↑' : '↓'} ${Math.abs(r).toFixed(1)}`;
}

/** Chip tone for a points delta: a change that rounds to 0.0 is neutral, never a down chip. */
export function ptsTone(d: number): 'up' | 'down' | 'neutral' {
  const r = Math.round(d * 10) / 10;
  return r === 0 ? 'neutral' : r > 0 ? 'up' : 'down';
}

/** Students whose bucket starts below the pass mark (buckets are named like "40-50"). */
export function belowPassCount(histogram: Bucket[], passMark: number): number {
  return histogram.reduce((n, b) => {
    const lower = parseFloat(b.bucket);
    return Number.isFinite(lower) && lower < passMark ? n + b.count : n;
  }, 0);
}

export function spreadSentence(p25: number, p75: number): string {
  return `Half the cohort scores between ${p25.toFixed(1)}% and ${p75.toFixed(1)}%.`;
}

/** Left offset and width of the interquartile band, and the median position, as CSS percentages. */
export function boxPlotGeometry(p25: number | null, median: number | null, p75: number | null) {
  const lo = p25 == null ? 0 : clamp(p25);
  const hi = p75 == null ? lo : clamp(p75);
  return {
    left: `${lo}%`,
    width: `${Math.max(0, hi - lo)}%`,
    median: median == null ? null : `${clamp(median)}%`,
  };
}
